import { useRef } from 'react';
import * as React from 'react';
import gettext from '@sdk/lang/gettext';
import TextField from '@sdk/components/forms/TextField';
import BodyPortal from '@sdk/common/tooltip/BodyPortal';
import { PickerMenuBodyPortal } from '@sdk/components/forms/pickerFieldBodyPortal.styles';
import { usePickerMenuPlacement } from '@sdk/components/forms/pickerFieldBodyPortal.hooks';
import { SpinnerSize } from '@sdk/components/ui/Spinner';

import { useGeoAddressField } from './geoAddressField.hooks';
import {
  Container,
  InputContainer,
  PickerItem,
  PickerItemText,
  Postfix,
  StyledSpiner,
} from './geoAddressField.styles';
import type { GeoAddress } from './geoAddressField.types';

type Props = {
  onChange: (value: GeoAddress) => void;
  customWidth?: number;
  value: string;
};

const GeoAddressField: React.FC<React.PropsWithChildren<Props>> = ({
  onChange,
  customWidth,
  value,
}) => {
  const pickerRef = useRef(null);
  const { updatePickerMenuPlacement, placement } = usePickerMenuPlacement(pickerRef);
  const {
    getComboboxProps,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    getInputProps,
    inputValue,
    isLoading,
    highlightedIndex,
    isOpen,
    addresses,
  } = useGeoAddressField({
    onChange,
    value,
    onIsOpenChange: ({ isOpen }) => {
      if (isOpen) {
        updatePickerMenuPlacement();
      }
    },
  });
  const { ref, ...inputProps } = getInputProps({ ref: pickerRef });

  return (
    <Container customWidth={customWidth} {...getComboboxProps()}>
      <InputContainer {...getToggleButtonProps()}>
        <TextField
          {...inputProps}
          ref={ref}
          skipErrorMessage
          value={inputValue}
          placeholder={gettext('e.g. 18 W 53rd St, New York, NY 10019, United States')}
        />
        <Postfix>{isLoading && <StyledSpiner size={SpinnerSize.small} />}</Postfix>
      </InputContainer>
      <BodyPortal>
        <PickerMenuBodyPortal zIndex={1501} placement={placement} {...getMenuProps()}>
          {isOpen &&
            addresses.map((item, index) => (
              <PickerItem
                key={index}
                highlighted={highlightedIndex === index}
                {...getItemProps({ item, index })}>
                <PickerItemText highlighted={highlightedIndex === index}>
                  {item.address}
                </PickerItemText>
              </PickerItem>
            ))}
        </PickerMenuBodyPortal>
      </BodyPortal>
    </Container>
  );
};

export default GeoAddressField;
